import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

const Navbar = () => {
  const { cartCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/shop", label: "Shop" },
    { href: "/admin", label: "Admin" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/40"
      data-testid="navbar"
    >
      <nav className="max-w-screen-2xl mx-auto flex items-center justify-between py-4 px-6 md:px-12">
        {/* Logo */}
        <Link 
          to="/" 
          className="font-serif text-xl md:text-2xl font-medium tracking-tight"
          data-testid="logo-link"
        >
          Kavya's Art
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm uppercase tracking-widest transition-colors hover:text-primary/70 ${
                isActive(link.href) ? "text-primary font-bold" : "text-muted-foreground"
              }`}
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right side icons */}
        <div className="flex items-center gap-4">
          <Link
            to="/cart"
            className="relative p-2 hover:bg-muted transition-colors"
            data-testid="cart-link"
          >
            <ShoppingBag className="w-5 h-5" />
            {cartCount > 0 && (
              <span 
                className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs w-5 h-5 flex items-center justify-center"
                data-testid="cart-count"
              >
                {cartCount}
              </span>
            )}
          </Link>

          {/* Mobile menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex flex-col gap-6 mt-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-lg uppercase tracking-widest ${
                      isActive(link.href) ? "text-primary font-bold" : "text-muted-foreground"
                    }`}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

const Footer = () => {
  return (
    <footer className="border-t border-border bg-muted/30 mt-24" data-testid="footer">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <h3 className="font-serif text-xl font-medium mb-4">Kavya's Art</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              A collection of digital artwork created with love by Kavya (and assisted by mom). 
              Each piece captures the unique beauty of every season.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Quick Links</h4>
            <div className="flex flex-col gap-2">
              <Link to="/shop" className="text-sm hover:text-primary/70 transition-colors">Shop All</Link>
              <Link to="/#collections" className="text-sm hover:text-primary/70 transition-colors">Collections</Link>
              <Link to="/#about" className="text-sm hover:text-primary/70 transition-colors">About Us</Link>
            </div>
          </div>

          {/* Policies */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Policies</h4>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>Digital downloads only - no returns</p>
              <p>Instant delivery to your email</p>
              <p>Secure payments via Stripe, PayPal, Razorpay</p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border text-center">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Kavya's Art. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Layout;
